import { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Card,
} from "react-bootstrap";
import {
  FaPlus,
  FaTrash,
  FaFolder,
  FaEdit,
  FaExternalLinkAlt,
  FaFileAlt,
} from "react-icons/fa";
import axios from "axios";
import DeleteModal from "../../components/modals/DeleteModal";
import SuccessModal from "../../components/modals/SuccessModal";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import CountryCodeSelect from "../../components/CountryCodeSelect";
import CountryDropdown from '../../components/CountryDropdown';

import "../../styles/ManageMarketplace.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

// Company code for marketplace
const MARKETPLACE_COMPANY_CODE = "1004";

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "blockquote",
  "code-block",
  "link",
  "image",
  "align",
  "color",
  "background",
];

export default function ManageMarketplace() {
  const [categories, setCategories] = useState([]);
  const [marketplaceCategories, setMarketplaceCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [marketplaceSearchTerm, setMarketplaceSearchTerm] = useState("");
  const [marketplaceSubCatSearchTerm, setMarketplaceSubCatSearchTerm] =
    useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [modalType, setModalType] = useState("primary"); // 'primary' or 'marketplace'
  const [formData, setFormData] = useState({ name: "" });
  const [selectedPrimaryCategoryId, setSelectedPrimaryCategoryId] =
    useState(null);
  const [deleteModalInfo, setDeleteModalInfo] = useState({
    id: null,
    confirmMessage: "",
    type: "primary",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState("");
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [selectedMarketplaceCategory, setSelectedMarketplaceCategory] =
    useState(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState({ name: "" });

  // Footer management states
  const [footerSections, setFooterSections] = useState([]);
  const [footerLoading, setFooterLoading] = useState(true);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedFooterId, setSelectedFooterId] = useState(null);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [deleteSectionId, setDeleteSectionId] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);

  // Marketplace details states
  const [marketplaceDetails, setMarketplaceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsForm, setDetailsForm] = useState({
    company_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    company_email: "",
    company_country_code: "+94",
    company_phone: "",
    support_wa_country_code: "+94",
    support_wa: "",
    support_enabled: false,
    company_logo: null,
  });
  const [logoPreview, setLogoPreview] = useState(null);

  const [sectionForm, setSectionForm] = useState({
    section_title: "",
    is_active: true,
  });

  const [itemForm, setItemForm] = useState({
    item_title: "",
    item_url: "",
    is_external_link: false,
    is_active: true,
    page_title: "",
    page_description: "",
    page_content: "",
  });

  // Policy management states
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState(false);
  const [deletePolicyId, setDeletePolicyId] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    title: "",
    description: "",
    is_active: true,
  });

  // About page states (support multiple sections)
  const [abouts, setAbouts] = useState([]);
  const [aboutLoading, setAboutLoading] = useState(true);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [editingAbout, setEditingAbout] = useState(null);
  const [aboutForm, setAboutForm] = useState({
    about_title: "",
    about_description: "",
    sort_order: 0,
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/categories/get-primary-categories`,
      );
      if (response.data.success) {
        // Map backend fields to frontend id/name format
        const mapped = response.data.categories.map((cat) => ({
          id: cat.primary_category_id,
          name: cat.primary_category_name,
          created_at: cat.created_at,
        }));
        setCategories(mapped);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setErrorMsg("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMarketplaceCategories = useCallback(async () => {
    setMarketplaceLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/categories/get_marketplace_categories`,
      );
      if (response.data.success) {
        const mapped = response.data.categories.map((cat) => ({
          id: cat.marketplace_category_id,
          name: cat.marketplace_category_name,
          parentId: cat.mp_parent_cat_id,
          primaryCategoryId: cat.primary_category_id,
          primaryCategoryName: cat.primary_category_name,
          created_at: cat.created_at,
          subcategories: [],
        }));

        // Fetch subcategories for each main category (only if parentId is null)
        const mappedWithSubcategories = await Promise.all(
          mapped.map(async (cat) => {
            // Only fetch subcategories for main categories
            if (cat.parentId === null || cat.parentId === undefined) {
              try {
                const subResponse = await axios.get(
                  `${BASE_URL}/api/admin/categories/marketplace_subcategories/${cat.id}`,
                );
                if (subResponse.data.success) {
                  cat.subcategories = subResponse.data.subcategories.map(
                    (sub) => sub.marketplace_category_name,
                  );
                }
              } catch (subError) {
                console.error(
                  `Error fetching subcategories for category ${cat.id}:`,
                  subError,
                );
              }
            }
            return cat;
          }),
        );

        setMarketplaceCategories(mappedWithSubcategories);
      }
    } catch (error) {
      console.error("Error fetching marketplace categories:", error);
      setErrorMsg("Failed to load marketplace categories. Please try again.");
    } finally {
      setMarketplaceLoading(false);
    }
  }, []);

  // Fetch footer sections for marketplace
  const fetchFooterSections = useCallback(async () => {
    try {
      setFooterLoading(true);
      const response = await axios.get(
        `${BASE_URL}/api/admin/footer/sections`,
        {
          params: { company_code: MARKETPLACE_COMPANY_CODE },
        },
      );

      if (response.data.success) {
        setFooterSections(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching footer sections:", err);
      setErrorMsg("Failed to fetch footer sections");
    } finally {
      setFooterLoading(false);
    }
  }, []);

  // Fetch marketplace details
  const fetchMarketplaceDetails = useCallback(async () => {
    try {
      setDetailsLoading(true);
      const response = await axios.get(
        `${BASE_URL}/api/admin/website/marketplace-details`,
      );

      if (response.data.success) {
        setMarketplaceDetails(response.data.data);
        setDetailsForm({
          company_name: response.data.data.company_name || "",
          address_line1: response.data.data.address_line1 || "",
          address_line2: response.data.data.address_line2 || "",
          city: response.data.data.city || "",
          state: response.data.data.state || "",
          country: response.data.data.country || "",
          postal_code: response.data.data.postal_code || "",
          company_email: response.data.data.company_email || "",
          company_country_code:
            response.data.data.company_country_code || "+94",
          company_phone: response.data.data.company_phone || "",
          support_wa_country_code:
            response.data.data.support_wa_country_code || "+94",
          support_wa: response.data.data.support_wa || "",
          support_enabled: response.data.data.support_enabled || false,
          company_logo: null,
        });
        if (response.data.data.company_logo) {
          setLogoPreview(
            `${BASE_URL}/uploads/company_logos/${response.data.data.company_logo}`,
          );
        }
      }
    } catch (err) {
      console.error("Error fetching marketplace details:", err);
      setErrorMsg("Failed to fetch marketplace details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Fetch policies
  const fetchPolicies = useCallback(async () => {
    try {
      setPoliciesLoading(true);
      const response = await axios.get(`${BASE_URL}/api/admin/policies`);

      if (response.data.success) {
        setPolicies(response.data.policies);
      }
    } catch (err) {
      console.error("Error fetching policies:", err);
      setErrorMsg("Failed to fetch policies");
    } finally {
      setPoliciesLoading(false);
    }
  }, []);

  // Fetch about page content for marketplace
  const fetchAbout = useCallback(async () => {
    setAboutLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/website/marketplace/about`,
      );
      if (response.data.success) {
        const data = response.data.data || [];
        setAbouts(Array.isArray(data) ? data : [data]);
      }
    } catch (err) {
      console.error("Error fetching about page:", err);
      setErrorMsg("Failed to fetch about page");
    } finally {
      setAboutLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchMarketplaceCategories();
    fetchFooterSections();
    fetchMarketplaceDetails();
    fetchPolicies();
    fetchAbout();
  }, [
    fetchCategories,
    fetchMarketplaceCategories,
    fetchFooterSections,
    fetchMarketplaceDetails,
    fetchPolicies,
    fetchAbout,
  ]);

  const filteredCategories = categories.filter((cat) =>
    (cat.name || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const filteredMarketplaceCategories = marketplaceCategories
    .filter((cat) => cat.parentId === null || cat.parentId === undefined)
    .filter(
      (cat) =>
        (cat.name || "")
          .toLowerCase()
          .includes(marketplaceSearchTerm.toLowerCase()) ||
        cat.subcategories.some((sub) =>
          sub.toLowerCase().includes(marketplaceSubCatSearchTerm.toLowerCase()),
        ),
    );

  // Footer management functions
  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingSection
        ? `${BASE_URL}/api/admin/footer/sections/${editingSection.footer_id}`
        : `${BASE_URL}/api/admin/footer/sections`;

      const method = editingSection ? "put" : "post";
      const data = editingSection
        ? sectionForm
        : { ...sectionForm, company_code: MARKETPLACE_COMPANY_CODE };

      const response = await axios[method](url, data);

      if (response.data.success) {
        setSuccessModalMessage(
          editingSection
            ? "Section updated successfully"
            : "Section created successfully",
        );
        setShowSuccessModal(true);
        setShowSectionModal(false);
        resetSectionForm();
        fetchFooterSections();
      }
    } catch (err) {
      console.error("Error saving section:", err);
      setErrorMsg("Failed to save section");
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingItem
        ? `${BASE_URL}/api/admin/footer/items/${editingItem.item_id}`
        : `${BASE_URL}/api/admin/footer/items`;

      const method = editingItem ? "put" : "post";

      let data = { ...itemForm };

      if (!editingItem) {
        data.footer_id = selectedFooterId;
      }

      if (!itemForm.is_external_link) {
        if (!itemForm.page_title || !itemForm.page_content) {
          setErrorMsg("Page title and content are required for internal links");
          return;
        }
        data.item_url = "";
      } else {
        if (!itemForm.item_url) {
          setErrorMsg("URL is required for external links");
          return;
        }
        data.page_title = "";
        data.page_content = "";
      }

      const response = await axios[method](url, data);

      if (response.data.success) {
        // show success modal instead of inline alert
        setSuccessModalMessage(
          editingItem
            ? "Item updated successfully"
            : "Item created successfully",
        );
        setShowSuccessModal(true);
        setShowItemModal(false);
        resetItemForm();
        fetchFooterSections();
      }
    } catch (err) {
      console.error("Error saving item:", err);
      setErrorMsg(err.response?.data?.message || "Failed to save item");
    }
  };

  const resetSectionForm = () => {
    setSectionForm({
      section_title: "",
      is_active: true,
    });
    setEditingSection(null);
  };

  const resetItemForm = () => {
    setItemForm({
      item_title: "",
      item_url: "",
      is_external_link: false,
      is_active: true,
      page_title: "",
      page_description: "",
      page_content: "",
    });
    setEditingItem(null);
    setSelectedFooterId(null);
  };

  const editSection = (section) => {
    setEditingSection(section);
    setSectionForm({
      section_title: section.section_title,
      is_active: section.section_active,
    });
    setShowSectionModal(true);
  };

  const editItem = async (item) => {
    setEditingItem(item);

    if (!item.is_external_link) {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/admin/footer/page-edit/${item.item_id}`,
        );
        if (response.data.success) {
          const pageData = response.data.data;
          setItemForm({
            item_title: item.item_title,
            item_url: item.item_url,
            is_external_link: item.is_external_link,
            is_active: item.item_active,
            page_title: pageData.page_title || "",
            page_description: pageData.page_description || "",
            page_content: pageData.page_content || "",
          });
        }
      } catch (err) {
        console.error("Error fetching page content:", err);
        setErrorMsg("Failed to fetch page content");
      }
    } else {
      setItemForm({
        item_title: item.item_title,
        item_url: item.item_url,
        is_external_link: item.is_external_link,
        is_active: item.item_active,
        page_title: "",
        page_description: "",
        page_content: "",
      });
    }

    setShowItemModal(true);
  };

  const addItem = (footerId) => {
    resetItemForm();
    setSelectedFooterId(footerId);
    setShowItemModal(true);
  };

  const handleLinkTypeChange = (isExternal) => {
    setItemForm({
      ...itemForm,
      is_external_link: isExternal,
      item_url: isExternal ? itemForm.item_url : "",
      page_title: isExternal ? "" : itemForm.page_title,
      page_content: isExternal ? "" : itemForm.page_content,
    });
  };

  const handleDeleteSectionClick = (footerId) => {
    setDeleteSectionId(footerId);
    setShowDeleteSectionModal(true);
  };

  const handleDeleteSectionSuccess = () => {
    setSuccessModalMessage("Section deleted successfully");
    setShowSuccessModal(true);
    fetchFooterSections();
  };

  const handleDeleteSectionError = (error) => {
    setErrorMsg(error?.message || "Failed to delete section");
  };

  const handleDeleteItemClick = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteItemModal(true);
  };

  const handleDeleteItemSuccess = () => {
    setSuccessModalMessage("Item deleted successfully");
    setShowSuccessModal(true);
    fetchFooterSections();
  };

  const handleDeleteItemError = (error) => {
    setErrorMsg(error?.message || "Failed to delete item");
  };

  // Policy management handlers
  const resetPolicyForm = () => {
    setPolicyForm({
      title: "",
      description: "",
      is_active: true,
    });
    setEditingPolicy(null);
  };

  const handleOpenPolicyModal = (policy = null) => {
    if (policy) {
      setEditingPolicy(policy);
      setPolicyForm({
        title: policy.title,
        description: policy.description,
        is_active: policy.is_active,
      });
    } else {
      resetPolicyForm();
    }
    setShowPolicyModal(true);
  };

  const handleClosePolicyModal = () => {
    setShowPolicyModal(false);
    resetPolicyForm();
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingPolicy
        ? `${BASE_URL}/api/admin/policies/${editingPolicy.id}`
        : `${BASE_URL}/api/admin/policies`;

      const method = editingPolicy ? "put" : "post";
      const response = await axios[method](url, policyForm);

      if (response.data.success) {
        setSuccessModalMessage(
          editingPolicy
            ? "Policy updated successfully"
            : "Policy created successfully",
        );
        setShowSuccessModal(true);
        handleClosePolicyModal();
        fetchPolicies();
      }
    } catch (err) {
      console.error("Error saving policy:", err);
      setErrorMsg(err.response?.data?.message || "Failed to save policy");
    }
  };

  const handleDeletePolicyClick = (policyId) => {
    setDeletePolicyId(policyId);
    setShowDeletePolicyModal(true);
  };

  const handleDeletePolicySuccess = () => {
    setSuccessModalMessage("Policy deleted successfully");
    setShowSuccessModal(true);
    fetchPolicies();
  };

  const handleDeletePolicyError = (error) => {
    setErrorMsg(error?.message || "Failed to delete policy");
  };

  const handleTogglePolicyStatus = async (policyId) => {
    try {
      const response = await axios.patch(
        `${BASE_URL}/api/admin/policies/${policyId}/toggle-status`,
      );
      if (response.data.success) {
        setSuccessModalMessage("Policy status updated successfully");
        setShowSuccessModal(true);
        fetchPolicies();
      }
    } catch (err) {
      console.error("Error toggling policy status:", err);
      setErrorMsg(
        err.response?.data?.message || "Failed to update policy status",
      );
    }
  };

  // About page handlers
  const handleOpenAboutModal = (section = null) => {
    setEditingAbout(section);
    setAboutForm({
      about_title: section?.about_title || "",
      about_description: section?.about_description || "",
      sort_order: section?.sort_order ?? abouts.length + 1,
    });
    setShowAboutModal(true);
  };

  const handleAboutSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editingAbout && editingAbout.id) {
        response = await axios.put(
          `${BASE_URL}/api/admin/website/marketplace/about/${editingAbout.id}`,
          aboutForm,
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/api/admin/website/marketplace/about`,
          aboutForm,
        );
      }

      if (response.data.success) {
        setSuccessModalMessage(
          editingAbout
            ? "Section updated successfully"
            : "Section created successfully",
        );
        setShowSuccessModal(true);
        setShowAboutModal(false);
        fetchAbout();
      } else {
        setErrorMsg(response.data.message || "Failed to save about section");
      }
    } catch (err) {
      console.error("Error saving about page:", err);
      setErrorMsg(
        err.response?.data?.message || "Failed to save about section",
      );
    } finally {
      setTimeout(() => setShowSuccessModal(false), 500);
    }
  };

  const handleToggleAboutStatus = async (sectionId) => {
    try {
      const response = await axios.patch(
        `${BASE_URL}/api/admin/website/marketplace/about/${sectionId}/toggle-status`,
      );
      if (response.data.success) {
        setSuccessModalMessage("Section status updated successfully");
        setShowSuccessModal(true);
        fetchAbout();
      }
    } catch (err) {
      console.error("Error toggling about status:", err);
      setErrorMsg(
        err.response?.data?.message || "Failed to update section status",
      );
    } finally {
      setTimeout(() => setShowSuccessModal(false), 500);
    }
  };

  // Marketplace details handlers
  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("company_name", detailsForm.company_name);
      formData.append("address_line1", detailsForm.address_line1);
      formData.append("address_line2", detailsForm.address_line2);
      formData.append("city", detailsForm.city);
      formData.append("state", detailsForm.state);
      formData.append("country", detailsForm.country);
      formData.append("postal_code", detailsForm.postal_code);
      formData.append("company_email", detailsForm.company_email);
      formData.append("company_country_code", detailsForm.company_country_code);
      formData.append("company_phone", detailsForm.company_phone);
      formData.append(
        "support_wa_country_code",
        detailsForm.support_wa_country_code,
      );
      formData.append("support_wa", detailsForm.support_wa);
      formData.append("support_enabled", detailsForm.support_enabled);

      if (detailsForm.company_logo) {
        formData.append("company_logo", detailsForm.company_logo);
      }

      // Use POST if no existing details, otherwise PUT
      const method = marketplaceDetails ? "put" : "post";
      const response = await axios[method](
        `${BASE_URL}/api/admin/website/marketplace-details`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        setSuccessModalMessage(
          marketplaceDetails
            ? "Marketplace details updated successfully"
            : "Marketplace details created successfully",
        );
        setShowSuccessModal(true);
        fetchMarketplaceDetails();
      }
    } catch (err) {
      console.error("Error saving marketplace details:", err);
      setErrorMsg(
        err.response?.data?.message || "Failed to save marketplace details",
      );
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDetailsForm({ ...detailsForm, company_logo: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenAddModal = (type = "primary") => {
    setModalType(type);
    setEditingCategory(null);
    setFormData({ name: "" });
    setSelectedPrimaryCategoryId(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (category, type = "primary") => {
    setModalType(type);
    setEditingCategory(category);
    setFormData({ name: category.name });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingCategory(null);
    setFormData({ name: "" });
    setSelectedPrimaryCategoryId(null);
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (modalType === "marketplace" && !selectedPrimaryCategoryId) {
      setErrorMsg("Please select a primary category");
      return;
    }

    try {
      let response;
      const isMarketplace = modalType === "marketplace";
      const baseEndpoint = isMarketplace
        ? "marketplace_category"
        : "primary_category";

      if (editingCategory) {
        response = await axios.put(
          `${BASE_URL}/api/admin/categories/update_${baseEndpoint}/${editingCategory.id}`,
          {
            category_name: formData.name.trim(),
          },
        );
      } else {
        const payload = {
          category_name: formData.name.trim(),
        };
        if (isMarketplace) {
          payload.primary_category_id = selectedPrimaryCategoryId;
        }
        response = await axios.post(
          `${BASE_URL}/api/admin/categories/add_${baseEndpoint}`,
          payload,
        );
      }

      if (response.data.success) {
        setSuccessModalMessage(response.data.message);
        setShowSuccessModal(true);
        if (isMarketplace) {
          fetchMarketplaceCategories();
        } else {
          fetchCategories();
        }
        handleCloseModal();
      } else {
        setErrorMsg(response.data.message);
      }
    } catch (error) {
      console.error("Error saving category:", error);
      setErrorMsg("Server error. Please try again.");
    } finally {
      setTimeout(() => setShowSuccessModal(false), 500);
    }
  };

  const handleDeleteClick = (category, type = "primary") => {
    const typeLabel =
      type === "marketplace" ? "marketplace category" : "primary category";
    const confirmMessage = `Are you sure you want to delete ${typeLabel} "${category.name}"? This action may affect product mappings.`;
    setDeleteModalInfo({ id: category.id, confirmMessage, type });
  };

  const handleOpenSubcategoryModal = (category) => {
    setSelectedMarketplaceCategory(category);
    setSubcategoryFormData({ name: "" });
    setShowSubcategoryModal(true);
  };

  const handleCloseSubcategoryModal = () => {
    setShowSubcategoryModal(false);
    setSelectedMarketplaceCategory(null);
    setSubcategoryFormData({ name: "" });
    setErrorMsg("");
  };

  const handleSubmitSubcategory = async (e) => {
    e.preventDefault();
    if (!subcategoryFormData.name.trim() || !selectedMarketplaceCategory)
      return;

    try {
      const response = await axios.post(
        `${BASE_URL}/api/admin/categories/add_mp_subcategory`,
        {
          marketplace_category_id: selectedMarketplaceCategory.id,
          subcategory_name: subcategoryFormData.name.trim(),
        },
      );

      if (response.data.success) {
        setSuccessModalMessage(
          response.data.message || "Subcategory added successfully",
        );
        setShowSuccessModal(true);
        fetchMarketplaceCategories();
        handleCloseSubcategoryModal();
      } else {
        setErrorMsg(response.data.message || "Failed to add subcategory");
      }
    } catch (error) {
      console.error("Error saving subcategory:", error);
      setErrorMsg("Server error. Please try again.");
    } finally {
      setTimeout(() => setShowSuccessModal(false), 500);
    }
  };

  const handleDeleteSuccess = () => {
    const type = deleteModalInfo.type;
    setDeleteModalInfo({ id: null, confirmMessage: "", type: "primary" });
    if (type === "marketplace") {
      fetchMarketplaceCategories();
    } else {
      fetchCategories();
    }
    setSuccessModalMessage("Category deleted successfully");
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 500);
  };

  return (
    <div className="marketplace-container mt-4">
      <div className="marketplace-header">
        <h1>Manage Marketplace</h1>
      </div>

      <SuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        message={successModalMessage}
      />
      {errorMsg && (
        <Alert variant="danger" dismissible onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      )}

      <div className="table-responsive">
        <div className="marketplace-sub-header">
          <h2>Manage Marketplace Categories</h2>
          <button
            className="btn-custom-primary add-marketplace-btn"
            onClick={() => handleOpenAddModal("marketplace")}
          >
            <FaPlus size={14} style={{ marginRight: "8px" }} />
            Add Marketplace Category
          </button>
        </div>
        {marketplaceLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading marketplace categories...</p>
          </div>
        ) : (
          <Table className="align-middle marketplace-table" hover>
            <thead>
              <tr>
                <th style={{ width: "50px" }}>#</th>
                <th>
                  <div className="d-flex flex-column gap-2">
                    <span>Primary Category Name</span>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search category..."
                      value={marketplaceSearchTerm}
                      onChange={(e) => setMarketplaceSearchTerm(e.target.value)}
                      style={{ fontSize: "12px", padding: "4px 8px" }}
                    />
                  </div>
                </th>
                <th>
                  <div className="d-flex flex-column gap-2">
                    <span>Marketplace Category Name</span>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search category..."
                      value={marketplaceSearchTerm}
                      onChange={(e) => setMarketplaceSearchTerm(e.target.value)}
                      style={{ fontSize: "12px", padding: "4px 8px" }}
                    />
                  </div>
                </th>
                <th>
                  <div className="d-flex flex-column gap-2">
                    <span>Marketplace Sub Category Names</span>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search category..."
                      value={marketplaceSubCatSearchTerm}
                      onChange={(e) =>
                        setMarketplaceSubCatSearchTerm(e.target.value)
                      }
                      style={{ fontSize: "12px", padding: "4px 8px" }}
                    />
                  </div>
                </th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMarketplaceCategories.length > 0 ? (
                filteredMarketplaceCategories.map((cat, index) => (
                  <tr key={cat.id}>
                    <td>{index + 1}</td>
                    <td style={{ cursor: "pointer" }}>
                      {cat.primaryCategoryName || (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td
                      // onClick={() => handleOpenEditModal(cat, "marketplace")}
                      style={{ cursor: "pointer" }}
                    >
                      {cat.name}
                    </td>
                    <td
                      // onClick={() => handleOpenEditModal(cat, "marketplace")}
                      style={{ cursor: "pointer" }}
                    >
                      {cat.subcategories && cat.subcategories.length > 0 ? (
                        cat.subcategories.join(", ")
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td
                      className="text-center align-middle"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <FaTrash
                        className="action-icon text-danger"
                        onClick={() => handleDeleteClick(cat, "marketplace")}
                        title="Delete"
                        style={{ cursor: "pointer", margin: "0 6px" }}
                      />
                      <FaFolder
                        className="action-icon text-primary"
                        onClick={() => handleOpenSubcategoryModal(cat)}
                        title="Add Subcategory"
                        style={{ cursor: "pointer", margin: "0 6px" }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-muted">
                    No marketplace categories found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Marketplace Details Management Section */}
      <div className="table-responsive">
        <div className="marketplace-sub-header">
          <h2>Manage Marketplace Details</h2>
        </div>

        {detailsLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading marketplace details...</p>
          </div>
        ) : (
          <Card className="mb-4">
            <Card.Body>
              <Form onSubmit={handleDetailsSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Group>
                      <Form.Label>Company Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={detailsForm.company_name}
                        onChange={(e) =>
                          setDetailsForm({
                            ...detailsForm,
                            company_name: e.target.value,
                          })
                        }
                        required
                        placeholder="Enter company name"
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Group>
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        value={detailsForm.company_email}
                        onChange={(e) =>
                          setDetailsForm({
                            ...detailsForm,
                            company_email: e.target.value,
                          })
                        }
                        required
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Group>
                      <Form.Label>Phone Number *</Form.Label>
                      <div className="d-flex gap-2">
                        <CountryCodeSelect
                          value={detailsForm.company_country_code}
                          onChange={(code) =>
                            setDetailsForm({
                              ...detailsForm,
                              company_country_code: code,
                            })
                          }
                          isRequired={true}
                          width="150px"
                        />
                        <Form.Control
                          type="tel"
                          value={detailsForm.company_phone}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              company_phone: e.target.value,
                            })
                          }
                          required
                          placeholder="Phone number"
                          style={{ flex: 1 }}
                        />
                      </div>
                    </Form.Group>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Group>
                      <Form.Label>WhatsApp Support Number</Form.Label>
                      <div className="d-flex gap-2">
                        <CountryCodeSelect
                          value={detailsForm.support_wa_country_code}
                          onChange={(code) =>
                            setDetailsForm({
                              ...detailsForm,
                              support_wa_country_code: code,
                            })
                          }
                          width="150px"
                        />
                        <Form.Control
                          type="tel"
                          value={detailsForm.support_wa}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              support_wa: e.target.value,
                            })
                          }
                          placeholder="WhatsApp number"
                          style={{ flex: 1 }}
                          disabled={!detailsForm.support_enabled}
                        />
                      </div>
                      <Form.Check
                        type="checkbox"
                        label="Enable WhatsApp Support"
                        checked={detailsForm.support_enabled}
                        onChange={(e) =>
                          setDetailsForm({
                            ...detailsForm,
                            support_enabled: e.target.checked,
                          })
                        }
                        className="mt-2"
                      />
                    </Form.Group>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>Address Line 1:</Form.Label>
                        <Form.Control
                          type="text"
                          value={detailsForm.address_line1}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              address_line1: e.target.value,
                            })
                          }
                          placeholder="Enter address line 1"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>Address Line 2:</Form.Label>
                        <Form.Control
                          type="text"
                          value={detailsForm.address_line2}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              address_line2: e.target.value,
                            })
                          }
                          placeholder="Enter address line 2"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>City:</Form.Label>
                        <Form.Control
                          type="text"
                          value={detailsForm.city}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              city: e.target.value,
                            })
                          }
                          placeholder="Enter city"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>State:</Form.Label>
                        <Form.Control
                          type="text"
                          value={detailsForm.state}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              state: e.target.value,
                            })
                          }
                          placeholder="Enter state"
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>Country:</Form.Label>
                        <CountryDropdown
                          value={detailsForm.country || ""}
                          onChange={(country) =>
                            setDetailsForm({ ...detailsForm, country: country })
                          }
                          placeholder="Select country"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <Form.Label>Postal Code:</Form.Label>
                        <Form.Control
                          type="text"
                          value={detailsForm.postal_code}
                          onChange={(e) =>
                            setDetailsForm({
                              ...detailsForm,
                              postal_code: e.target.value,
                            })
                          }
                          placeholder="Enter postal code"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="col-md-12 mb-3">
                    <Form.Group>
                      <Form.Label>Company Logo</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      <Form.Text className="text-muted">
                        Upload a new logo (JPG, PNG, or GIF)
                      </Form.Text>
                      {logoPreview && (
                        <div className="mt-3">
                          <img
                            src={logoPreview}
                            alt="Logo Preview"
                            style={{
                              maxWidth: "200px",
                              maxHeight: "100px",
                              objectFit: "contain",
                              border: "1px solid #ddd",
                              padding: "10px",
                              borderRadius: "8px",
                              backgroundColor: "#f8f9fa",
                            }}
                          />
                        </div>
                      )}
                    </Form.Group>
                  </div>

                  <div className="col-md-12">
                    <Button variant="primary" type="submit" className="px-4">
                      Save Marketplace Details
                    </Button>
                  </div>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Marketplace Policies */}
      <div className="table-responsive">
        <div className="marketplace-sub-header">
          <h2>Manage Policies</h2>
          <button
            className="btn-custom-primary add-marketplace-btn"
            onClick={() => handleOpenPolicyModal()}
          >
            <FaPlus size={14} style={{ marginRight: "8px" }} />
            Add Policy
          </button>
        </div>

        {policiesLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading policies...</p>
          </div>
        ) : (
          <div className="policies-container">
            {policies.length > 0 ? (
              policies.map((policy) => (
                <Card key={policy.id} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-0"> {policy.title} </h5>
                      <small className="text-muted">
                        Last updated:{" "}
                        {new Date(policy.updated_at).toLocaleDateString()}
                      </small>
                    </div>
                    <div
                      style={{
                        gap: "10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Form.Check
                        type="switch"
                        id={`policy-switch-${policy.id}`}
                        checked={policy.is_active}
                        onChange={() => handleTogglePolicyStatus(policy.id)}
                        title={policy.is_active ? "Deactivate" : "Activate"}
                      />
                      <FaEdit
                        className="action-icon text-primary me-2"
                        onClick={() => handleOpenPolicyModal(policy)}
                        style={{ cursor: "pointer" }}
                        title="Edit Policy"
                      />
                      <FaTrash
                        className="action-icon text-danger"
                        onClick={() => handleDeletePolicyClick(policy.id)}
                        style={{ cursor: "pointer" }}
                        title="Delete Policy"
                      />
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <style>{`
                        .policy-description { font-size: 14px; }
                        .policy-description h1,
                        .policy-description h2 { font-size: 1.05rem; }
                        .policy-description h3 { font-size: 1rem; }
                        .policy-description h4,
                        .policy-description h5,
                        .policy-description h6 { font-size: 0.95rem; }
                        .policy-description p { margin: 0.45rem 0; }
                      `}</style>
                    <div
                      className="policy-description"
                      dangerouslySetInnerHTML={{ __html: policy.description }}
                      style={{
                        maxHeight: "200px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    />
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div className="text-center py-4 text-muted">
                <p>
                  No policies found. Create your first policy to get started.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manage About Page */}
      <div className="table-responsive">
        <div className="marketplace-sub-header">
          <h2>Manage About Page</h2>
          <button
            className="btn-custom-primary add-marketplace-btn"
            onClick={handleOpenAboutModal}
          >
            <FaPlus size={14} style={{ marginRight: "8px" }} />
            Add About Content
          </button>
        </div>

        {aboutLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading about page content...</p>
          </div>
        ) : (
          <div className="about-sections-container">
            {abouts.length > 0 ? (
              abouts.map((section) => (
                <Card key={section.id} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-0">{section.about_title}</h5>
                      <small className="text-muted">
                        Last updated:{" "}
                        {section.updated_at
                          ? new Date(section.updated_at).toLocaleDateString()
                          : "-"}
                      </small>
                    </div>
                    <div
                      style={{
                        gap: "10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Form.Check
                        type="switch"
                        id={`about-switch-${section.id}`}
                        checked={!!section.is_active}
                        onChange={() => handleToggleAboutStatus(section.id)}
                        title={section.is_active ? "Deactivate" : "Activate"}
                        className="me-2"
                      />
                      <FaEdit
                        className="action-icon text-primary"
                        title="Edit"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleOpenAboutModal(section)}
                      />
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          section.about_description ||
                          '<p className="text-muted">No content</p>',
                      }}
                    />
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div className="text-center py-4 text-muted">
                <p>
                  No about sections found. Create your first section to get
                  started.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Marketplace Footer Management Section */}
      <div className="table-responsive">
        <div className="marketplace-sub-header">
          <h2>Manage Marketplace Footer</h2>
          <button
            className="btn-custom-primary add-marketplace-btn"
            onClick={() => setShowSectionModal(true)}
          >
            <FaPlus size={14} style={{ marginRight: "8px" }} />
            Add Footer Section
          </button>
        </div>

        {footerLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading footer sections...</p>
          </div>
        ) : (
          <div className="footer-sections-container">
            {footerSections.length > 0 ? (
              footerSections.map((section) => (
                <Card key={section.footer_id} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-0">
                        {section.section_title}
                        <small
                          style={{
                            marginLeft: "10px",
                            color: section.section_active
                              ? "#155724"
                              : "#6c757d",
                            fontWeight: "bold",
                          }}
                        >
                          {section.section_active ? "Active" : "Inactive"}
                        </small>
                      </h5>
                    </div>
                    <div
                      style={{
                        gap: "10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        size="sm"
                        className="me-2 marketplace-add-item-btn"
                        onClick={() => addItem(section.footer_id)}
                      >
                        <FaPlus /> Add Item
                      </Button>
                      <FaEdit
                        className="action-icon text-primary me-2"
                        onClick={() => editSection(section)}
                        style={{ cursor: "pointer" }}
                        title="Edit Section"
                      />
                      <FaTrash
                        className="action-icon text-danger"
                        onClick={() =>
                          handleDeleteSectionClick(section.footer_id)
                        }
                        style={{ cursor: "pointer" }}
                        title="Delete Section"
                      />
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {section.items && section.items.length > 0 ? (
                      <Table responsive size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>URL/Page</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.items.map((item) => (
                            <tr key={item.item_id}>
                              <td>{item.item_title}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  {item.is_external_link ? (
                                    <FaExternalLinkAlt className="me-2 text-warning" />
                                  ) : (
                                    <FaFileAlt className="me-2 text-info" />
                                  )}
                                  <code style={{ fontSize: "12px" }}>
                                    {item.item_url || "Generated from title"}
                                  </code>
                                </div>
                              </td>
                              <td>
                                <span
                                  style={{
                                    color: item.is_external_link
                                      ? "#856404"
                                      : "#004085",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.is_external_link
                                    ? "External"
                                    : "Internal"}
                                </span>
                              </td>
                              <td>
                                <span
                                  style={{
                                    color: item.item_active
                                      ? "#155724"
                                      : "#6c757d",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.item_active ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td
                                className="text-center align-middle"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <FaEdit
                                  className="action-icon text-primary"
                                  onClick={() => editItem(item)}
                                  style={{
                                    cursor: "pointer",
                                    margin: "0 6px",
                                  }}
                                  title="Edit Item"
                                />
                                <FaTrash
                                  className="action-icon text-danger"
                                  onClick={() =>
                                    handleDeleteItemClick(item.item_id)
                                  }
                                  style={{
                                    cursor: "pointer",
                                    margin: "0 6px",
                                  }}
                                  title="Delete Item"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted mb-0">
                        No items in this section
                      </p>
                    )}
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div className="text-center py-4 text-muted">
                <p>
                  No footer sections found. Create your first section to get
                  started.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        show={showAddModal}
        onHide={handleCloseModal}
        centered
        dialogClassName="marketplace-modal-dialog w-75"
      >
        <Modal.Header className="marketplace-modal-header" closeButton>
          <Modal.Title>
            {editingCategory
              ? `Edit ${
                  modalType === "marketplace" ? "Marketplace" : "Primary"
                } Category`
              : `Add ${
                  modalType === "marketplace" ? "Marketplace" : "Primary"
                } Category`}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalType === "marketplace" && !editingCategory && (
              <Form.Group className="mb-4">
                <Form.Label className="form-label-premium">
                  Primary Category *
                </Form.Label>
                <Form.Select
                  className="form-control-premium"
                  value={selectedPrimaryCategoryId || ""}
                  onChange={(e) =>
                    setSelectedPrimaryCategoryId(e.target.value || null)
                  }
                  required
                >
                  <option value="">Select a primary category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
                <small className="text-muted d-block mt-2">
                  Select an existing primary category to associate this
                  marketplace category with
                </small>
              </Form.Group>
            )}
            <Form.Group className="mb-4">
              <Form.Label className="form-label-premium">
                {modalType === "marketplace" ? "Marketplace" : "Primary"}{" "}
                Category Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={`Type ${
                  modalType === "marketplace" ? "marketplace" : "primary"
                } category name`}
                className="form-control-premium"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                autoFocus
              />
            </Form.Group>

            <div className="p-3 bg-light rounded-3 d-flex align-items-start gap-3">
              <i className="bi bi-info-circle text-primary mt-1"></i>
              <small className="text-muted">
                {modalType === "marketplace"
                  ? "Adding a marketplace category helps organize products on the marketplace for better customer discovery."
                  : "Adding a primary category allows companies to map their internal categories to this one for better filtering and discovery."}
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={handleCloseModal} className="px-4">
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="px-4">
              {editingCategory ? "Update Changes" : "Create Category"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        id={deleteModalInfo.id}
        show={!!deleteModalInfo.id}
        onHide={() =>
          setDeleteModalInfo({
            id: null,
            confirmMessage: "",
            type: "primary",
          })
        }
        deleteUrl={(id) =>
          id
            ? `/api/admin/categories/delete_${
                deleteModalInfo.type === "marketplace"
                  ? "marketplace"
                  : "primary"
              }_category/${id}`
            : ""
        }
        entityLabel={
          deleteModalInfo.type === "marketplace"
            ? "Marketplace Category"
            : "Primary Category"
        }
        confirmMessage={deleteModalInfo.confirmMessage}
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* Add Subcategory Modal */}
      <Modal
        show={showSubcategoryModal}
        onHide={handleCloseSubcategoryModal}
        centered
        dialogClassName="marketplace-modal-dialog w-75"
      >
        <Modal.Header className="marketplace-modal-header" closeButton>
          <Modal.Title>
            Add Subcategory to {selectedMarketplaceCategory?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitSubcategory}>
          <Modal.Body>
            <Form.Group className="mb-4">
              <Form.Label className="form-label-premium">
                Marketplace Category
              </Form.Label>
              <Form.Control
                type="text"
                className="form-control-premium"
                value={selectedMarketplaceCategory?.name || ""}
                disabled
              />
              <small className="text-muted d-block mt-2">
                Selected marketplace category for this subcategory
              </small>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="form-label-premium">
                Subcategory Name *
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Type subcategory name"
                className="form-control-premium"
                value={subcategoryFormData.name}
                onChange={(e) =>
                  setSubcategoryFormData({
                    ...subcategoryFormData,
                    name: e.target.value,
                  })
                }
                required
                autoFocus
              />
            </Form.Group>

            <div className="p-3 bg-light rounded-3 d-flex align-items-start gap-3">
              <i className="bi bi-info-circle text-primary mt-1"></i>
              <small className="text-muted">
                Subcategories help further organize products within the selected
                marketplace category for better customer navigation and
                discovery.
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button
              variant="light"
              onClick={handleCloseSubcategoryModal}
              className="px-4"
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="px-4">
              Add Subcategory
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Footer Section Modal */}
      <Modal
        show={showSectionModal}
        onHide={() => {
          setShowSectionModal(false);
          resetSectionForm();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingSection ? "Edit Section" : "Add Footer Section"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSectionSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Section Title *</Form.Label>
              <Form.Control
                type="text"
                value={sectionForm.section_title}
                onChange={(e) =>
                  setSectionForm({
                    ...sectionForm,
                    section_title: e.target.value,
                  })
                }
                required
                placeholder="e.g., Corporate Info, Customer Service"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={sectionForm.is_active}
                onChange={(e) =>
                  setSectionForm({
                    ...sectionForm,
                    is_active: e.target.checked,
                  })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSectionModal(false);
                resetSectionForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingSection ? "Update" : "Create"} Section
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Footer Item Modal */}
      <Modal
        show={showItemModal}
        onHide={() => {
          setShowItemModal(false);
          resetItemForm();
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingItem ? "Edit Item" : "Add Item"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleItemSubmit}>
          <Modal.Body>
            <Tabs defaultActiveKey="basic" id="item-tabs">
              <Tab eventKey="basic" title="Basic Info">
                <div className="pt-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Item Title *</Form.Label>
                    <Form.Control
                      type="text"
                      value={itemForm.item_title}
                      onChange={(e) =>
                        setItemForm({
                          ...itemForm,
                          item_title: e.target.value,
                        })
                      }
                      required
                      placeholder="e.g., About Us, FAQ, Facebook"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Link Type *</Form.Label>
                    <div>
                      <Form.Check
                        type="radio"
                        id="internal-link"
                        name="linkType"
                        label="Internal Page (with content)"
                        checked={!itemForm.is_external_link}
                        onChange={() => handleLinkTypeChange(false)}
                      />
                      <Form.Check
                        type="radio"
                        id="external-link"
                        name="linkType"
                        label="External Link"
                        checked={itemForm.is_external_link}
                        onChange={() => handleLinkTypeChange(true)}
                      />
                    </div>
                  </Form.Group>

                  {itemForm.is_external_link && (
                    <Form.Group className="mb-3">
                      <Form.Label>External URL *</Form.Label>
                      <Form.Control
                        type="url"
                        value={itemForm.item_url}
                        onChange={(e) =>
                          setItemForm({
                            ...itemForm,
                            item_url: e.target.value,
                          })
                        }
                        placeholder="https://facebook.com/yourcompany"
                        required
                      />
                      <Form.Text className="text-muted">
                        Enter the full URL including https://
                      </Form.Text>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Active"
                      checked={itemForm.is_active}
                      onChange={(e) =>
                        setItemForm({
                          ...itemForm,
                          is_active: e.target.checked,
                        })
                      }
                    />
                  </Form.Group>
                </div>
              </Tab>

              {!itemForm.is_external_link && (
                <Tab eventKey="content" title="Page Content">
                  <div className="pt-3">
                    <Form.Group className="mb-3">
                      <Form.Label>Page Title *</Form.Label>
                      <Form.Control
                        type="text"
                        value={itemForm.page_title}
                        onChange={(e) =>
                          setItemForm({
                            ...itemForm,
                            page_title: e.target.value,
                          })
                        }
                        placeholder="Title for the page"
                        required={!itemForm.is_external_link}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Page Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={itemForm.page_description}
                        onChange={(e) =>
                          setItemForm({
                            ...itemForm,
                            page_description: e.target.value,
                          })
                        }
                        placeholder="Brief description of the page"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Page Content *</Form.Label>
                      <ReactQuill
                        theme="snow"
                        value={itemForm.page_content}
                        onChange={(content) =>
                          setItemForm({ ...itemForm, page_content: content })
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Write your page content here. Use the toolbar above for formatting."
                        style={{
                          height: "300px",
                          marginBottom: "50px",
                        }}
                      />
                    </Form.Group>
                  </div>
                </Tab>
              )}
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowItemModal(false);
                resetItemForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingItem ? "Update" : "Create"} Item
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Section Modal */}
      <DeleteModal
        show={showDeleteSectionModal}
        onHide={() => setShowDeleteSectionModal(false)}
        id={deleteSectionId}
        deleteUrl={`/api/admin/footer/sections/:id`}
        entityLabel="section"
        modalTitle="Delete Footer Section"
        confirmMessage="Are you sure you want to delete this section? This will also delete all items in this section."
        onDeleteSuccess={handleDeleteSectionSuccess}
        onDeleteError={handleDeleteSectionError}
      />

      {/* Delete Item Modal */}
      <DeleteModal
        show={showDeleteItemModal}
        onHide={() => setShowDeleteItemModal(false)}
        id={deleteItemId}
        deleteUrl={`/api/admin/footer/items/:id`}
        entityLabel="item"
        modalTitle="Delete Footer Item"
        confirmMessage="Are you sure you want to delete this item?"
        onDeleteSuccess={handleDeleteItemSuccess}
        onDeleteError={handleDeleteItemError}
      />

      {/* Policy Modal */}
      <Modal
        show={showPolicyModal}
        onHide={handleClosePolicyModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPolicy ? "Edit Policy" : "Add Policy"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePolicySubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Policy Title *</Form.Label>
              <Form.Control
                type="text"
                value={policyForm.title}
                onChange={(e) =>
                  setPolicyForm({ ...policyForm, title: e.target.value })
                }
                required
                placeholder="e.g., Privacy Policy, Terms of Service, Return Policy"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Policy Description *</Form.Label>
              <ReactQuill
                theme="snow"
                value={policyForm.description}
                onChange={(content) =>
                  setPolicyForm({ ...policyForm, description: content })
                }
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your policy content here. Use the toolbar above for formatting."
                style={{
                  height: "300px",
                  marginBottom: "50px",
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={policyForm.is_active}
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    is_active: e.target.checked,
                  })
                }
              />
              <Form.Text className="text-muted">
                Inactive policies will not be displayed on the marketplace.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClosePolicyModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingPolicy ? "Update" : "Create"} Policy
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Policy Modal */}
      {/* About Page Modal */}
      <Modal
        show={showAboutModal}
        onHide={() => setShowAboutModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Content Title & Description</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAboutSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Content Title *</Form.Label>
              <Form.Control
                type="text"
                value={aboutForm.about_title}
                onChange={(e) =>
                  setAboutForm({ ...aboutForm, about_title: e.target.value })
                }
                required
                placeholder="Content title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <ReactQuill
                theme="snow"
                value={aboutForm.about_description}
                onChange={(content) =>
                  setAboutForm({ ...aboutForm, about_description: content })
                }
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write the about page content here"
                style={{ height: "300px", marginBottom: "30px" }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sort Order</Form.Label>
              <Form.Control
                type="number"
                value={aboutForm.sort_order}
                onChange={(e) =>
                  setAboutForm({
                    ...aboutForm,
                    sort_order: Number(e.target.value),
                  })
                }
                min={0}
              />
              <Form.Text className="text-muted">
                Lower numbers appear first.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAboutModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <DeleteModal
        show={showDeletePolicyModal}
        onHide={() => setShowDeletePolicyModal(false)}
        id={deletePolicyId}
        deleteUrl={`/api/admin/policies/:id`}
        entityLabel="policy"
        modalTitle="Delete Policy"
        confirmMessage="Are you sure you want to delete this policy? This action cannot be undone."
        onDeleteSuccess={handleDeletePolicySuccess}
        onDeleteError={handleDeletePolicyError}
      />
    </div>
  );
}